/**
 * Admin Dashboard
 *
 * Tracks user sessions: location, device, OS, browser, export stats.
 * Connects to the local backend at http://localhost:3001.
 * Requires admin token (default: overlayStudioAdmin2026 — set ADMIN_TOKEN in backend .env).
 */

import { useState, useEffect, useCallback } from 'react';

const BACKEND_URL = 'http://localhost:3001';

// ─── Types ───────────────────────────────────────────────────────────────────

interface Stats {
  totalSessions: number;
  todaySessions: number;
  uniqueIpsToday: number;
  totalExports: number;
  avgJobDurationMs: number;
  topCountries: { country: string; countryCode: string; count: number }[];
  deviceBreakdown: { deviceType: string; count: number }[];
  osBreakdown: { os: string; count: number }[];
  browserBreakdown: { browser: string; count: number }[];
  resolutionBreakdown: { resolution: string; count: number }[];
  sessionsByDay: { date: string; count: number }[];
}

interface Session {
  id: string;
  timestamp: string;
  ip: string;
  country: string;
  countryCode: string;
  city: string;
  deviceType: string;
  os: string;
  osVersion: string;
  browser: string;
  browserVersion: string;
  endpoint: string;
  exportResolution?: string;
  exportQuality?: string;
  jobDurationMs?: number;
}

interface EncoderInfo {
  encoder: string;
  codec: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const FLAG_BASE = 'https://flagcdn.com/16x12';

const CountryFlag = ({ code }: { code: string }) => (
  <img
    src={`${FLAG_BASE}/${code.toLowerCase()}.png`}
    alt={code}
    className="inline-block w-4 h-3 mr-1 rounded-sm"
    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
  />
);

const DEVICE_ICONS: Record<string, string> = {
  Desktop: '🖥️', Mobile: '📱', Tablet: '📱',
};

const OS_COLORS: Record<string, string> = {
  macOS: '#555555', Windows: '#0078D4', Linux: '#FCC624',
  iOS: '#555555', Android: '#3DDC84', Unknown: '#6b7280',
};

const BROWSER_COLORS: Record<string, string> = {
  Chrome: '#4285F4', Safari: '#006CFF', Firefox: '#FF7139',
  Edge: '#0078D4', Opera: '#FF1B2D', Unknown: '#6b7280',
};

const DonutChart = ({
  data,
  colors,
  size = 120,
}: {
  data: { label: string; count: number }[];
  colors: Record<string, string>;
  size?: number;
}) => {
  const total = data.reduce((s, d) => s + d.count, 0);
  if (total === 0) return <div className="text-editor-text-muted text-xs">No data yet</div>;

  let cumulative = 0;
  const r = 45;
  const cx = 60;
  const cy = 60;
  const circumference = 2 * Math.PI * r;

  const slices = data.map((d) => {
    const pct = d.count / total;
    const offset = circumference - cumulative * circumference;
    const dash = pct * circumference;
    const color = colors[d.label] ?? '#6b7280';
    cumulative += pct;
    return { ...d, pct, offset, dash, color };
  });

  return (
    <div className="flex items-center gap-4">
      <svg width={size} height={size} viewBox="0 0 120 120">
        {slices.map((s, i) => (
          <circle
            key={i}
            cx={cx} cy={cy} r={r}
            fill="none"
            stroke={s.color}
            strokeWidth="18"
            strokeDasharray={`${s.dash} ${circumference - s.dash}`}
            strokeDashoffset={s.offset}
            transform="rotate(-90 60 60)"
            style={{ transition: 'stroke-dasharray 0.5s ease' }}
          />
        ))}
        <text x="60" y="63" textAnchor="middle" fill="#e2e8f0" fontSize="14" fontWeight="600">
          {total}
        </text>
      </svg>
      <div className="flex flex-col gap-1">
        {slices.map((s, i) => (
          <div key={i} className="flex items-center gap-2 text-xs text-editor-text-secondary">
            <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: s.color }} />
            <span className="truncate max-w-[90px]">{s.label}</span>
            <span className="text-editor-text-muted ml-auto">{(s.pct * 100).toFixed(0)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const BarChart = ({
  data,
  max,
  color = '#818cf8',
}: {
  data: { label: string; count: number }[];
  max: number;
  color?: string;
}) => {
  if (data.length === 0) return <div className="text-editor-text-muted text-xs">No data yet</div>;
  return (
    <div className="flex flex-col gap-2">
      {data.map((d, i) => (
        <div key={i} className="flex items-center gap-2 text-xs">
          <span className="w-24 text-editor-text-secondary truncate text-right">{d.label}</span>
          <div className="flex-1 bg-white/5 rounded-full h-4 overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{ width: `${(d.count / max) * 100}%`, background: color }}
            />
          </div>
          <span className="w-8 text-editor-text-muted">{d.count}</span>
        </div>
      ))}
    </div>
  );
};

// Mini sparkline
const Sparkline = ({ data }: { data: { date: string; count: number }[] }) => {
  if (data.length < 2) return null;
  const max = Math.max(...data.map(d => d.count), 1);
  const w = 200;
  const h = 40;
  const pts = data.map((d, i) => {
    const x = (i / (data.length - 1)) * w;
    const y = h - (d.count / max) * h;
    return `${x},${y}`;
  });
  return (
    <svg width={w} height={h} className="opacity-70">
      <polyline
        points={pts.join(' ')}
        fill="none"
        stroke="#818cf8"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────

export function AdminPage() {
  const [token, setToken] = useState(() => sessionStorage.getItem('adminToken') ?? '');
  const [tokenInput, setTokenInput] = useState('');
  const [authed, setAuthed] = useState(false);
  const [authError, setAuthError] = useState('');

  const [stats, setStats] = useState<Stats | null>(null);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [sessionTotal, setSessionTotal] = useState(0);
  const [encoder, setEncoder] = useState<EncoderInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'sessions' | 'map'>('overview');
  const [page, setPage] = useState(1);
  const [backendOnline, setBackendOnline] = useState(false);

  // ── Auth ──────────────────────────────────────────────────────────────────

  const login = async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/auth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: tokenInput }),
      });
      if (r.ok) {
        setToken(tokenInput);
        sessionStorage.setItem('adminToken', tokenInput);
        setAuthed(true);
        setAuthError('');
      } else {
        setAuthError('Invalid admin token');
      }
    } catch {
      setAuthError('Backend offline — start the local backend server first');
    }
  };

  // ── Data Fetching ────────────────────────────────────────────────────────

  const authHeaders = { 'x-admin-token': token };

  const fetchAll = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [statsRes, sessionsRes, encoderRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/stats`, { headers: authHeaders }),
        fetch(`${BACKEND_URL}/api/admin/sessions?page=${page}&limit=30`, { headers: authHeaders }),
        fetch(`${BACKEND_URL}/api/admin/encoder`, { headers: authHeaders }),
      ]);

      if (statsRes.status === 401) { setAuthed(false); return; }

      const statsData = await statsRes.json();
      const sessionsData = await sessionsRes.json();
      const encoderData = await encoderRes.json();

      if (statsData.success) setStats(statsData.data);
      if (sessionsData.success) {
        setSessions(sessionsData.data.sessions);
        setSessionTotal(sessionsData.data.total);
      }
      if (encoderData.success) setEncoder(encoderData.data);
      setAuthed(true);
      setBackendOnline(true);
    } catch {
      setBackendOnline(false);
    } finally {
      setLoading(false);
    }
  }, [token, page]);

  useEffect(() => {
    // Try auto-login with stored token
    if (token) {
      fetchAll();
    }
    // Check backend every 30s
    const interval = setInterval(() => {
      if (token) fetchAll();
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchAll, token]);

  useEffect(() => {
    // Check if backend is reachable
    fetch(`${BACKEND_URL}/api/health`)
      .then(r => r.ok && setBackendOnline(true))
      .catch(() => setBackendOnline(false));
  }, []);

  // ── Login Screen ─────────────────────────────────────────────────────────

  if (!authed || !token) {
    return (
      <div className="min-h-screen bg-editor-bg flex items-center justify-center">
        <div className="bg-white/5 border border-white/10 rounded-2xl p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <div className="text-4xl mb-3">🎬</div>
            <h1 className="text-2xl font-bold text-white">Overlay Studio</h1>
            <p className="text-editor-text-muted text-sm mt-1">Admin Dashboard</p>
          </div>

          {!backendOnline && (
            <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3 mb-4 text-yellow-400 text-xs">
              ⚠️ Backend is offline. Start the backend server with <code className="bg-white/10 px-1 py-0.5 rounded">npm run dev:backend</code>
            </div>
          )}

          <div className="flex flex-col gap-3">
            <input
              type="password"
              value={tokenInput}
              onChange={e => setTokenInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && login()}
              placeholder="Admin token"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-editor-text-muted focus:outline-none focus:border-indigo-500 text-sm"
            />
            {authError && <p className="text-red-400 text-xs">{authError}</p>}
            <button
              onClick={login}
              className="w-full bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg py-3 font-medium text-sm transition-colors"
            >
              Sign In
            </button>
          </div>

          <p className="text-editor-text-muted text-xs text-center mt-6">
            Default token: <code className="bg-white/10 px-1 py-0.5 rounded">overlayStudioAdmin2026</code><br />
            Set <code className="bg-white/10 px-1 py-0.5 rounded">ADMIN_TOKEN</code> env var to change it
          </p>
        </div>
      </div>
    );
  }

  // ── Dashboard ─────────────────────────────────────────────────────────────

  const StatCard = ({
    label, value, sub, icon,
  }: { label: string; value: string | number; sub?: string; icon: string }) => (
    <div className="bg-white/5 border border-white/10 rounded-xl p-4">
      <div className="flex items-start justify-between mb-2">
        <span className="text-editor-text-muted text-xs uppercase tracking-wide">{label}</span>
        <span className="text-2xl">{icon}</span>
      </div>
      <div className="text-2xl font-bold text-white">{value}</div>
      {sub && <div className="text-xs text-editor-text-muted mt-1">{sub}</div>}
    </div>
  );

  const maxCountry = Math.max(...(stats?.topCountries.map(c => c.count) ?? [1]));
  const maxSession = Math.max(...(stats?.sessionsByDay.map(d => d.count) ?? [1]));

  return (
    <div className="min-h-screen bg-editor-bg text-white">
      {/* Header */}
      <div className="border-b border-white/10 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <a href="/" className="text-editor-text-muted hover:text-white text-sm transition-colors">← Back to Editor</a>
          <span className="text-white/20">|</span>
          <h1 className="text-lg font-semibold">Admin Dashboard</h1>
          {loading && <span className="text-indigo-400 text-xs animate-pulse">Refreshing…</span>}
        </div>
        <div className="flex items-center gap-3">
          {encoder && (
            <div className="flex items-center gap-1.5 bg-green-500/10 border border-green-500/20 rounded-full px-3 py-1">
              <span className="w-1.5 h-1.5 rounded-full bg-green-400" />
              <span className="text-green-400 text-xs">{encoder.encoder}</span>
            </div>
          )}
          <div className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs ${backendOnline ? 'bg-green-500/10 border border-green-500/20 text-green-400' : 'bg-red-500/10 border border-red-500/20 text-red-400'}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${backendOnline ? 'bg-green-400' : 'bg-red-400'}`} />
            {backendOnline ? 'Backend Online' : 'Backend Offline'}
          </div>
          <button
            onClick={fetchAll}
            className="text-xs text-editor-text-muted hover:text-white px-3 py-1 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
          >
            ↻ Refresh
          </button>
          <button
            onClick={() => { setAuthed(false); setToken(''); sessionStorage.removeItem('adminToken'); }}
            className="text-xs text-editor-text-muted hover:text-red-400 px-3 py-1 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
          >
            Sign Out
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-white/10 px-6 flex gap-1">
        {(['overview', 'sessions', 'map'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-3 text-sm font-medium capitalize transition-colors border-b-2 -mb-px ${
              activeTab === tab
                ? 'border-indigo-500 text-white'
                : 'border-transparent text-editor-text-muted hover:text-white'
            }`}
          >
            {tab === 'overview' ? '📊 Overview' : tab === 'sessions' ? '📋 Sessions' : '🌍 Map'}
          </button>
        ))}
      </div>

      <div className="p-6">
        {/* ── OVERVIEW TAB ── */}
        {activeTab === 'overview' && (
          <div className="flex flex-col gap-6">
            {/* Stat Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard icon="👥" label="Total Sessions" value={stats?.totalSessions ?? '—'} />
              <StatCard icon="📅" label="Today's Sessions" value={stats?.todaySessions ?? '—'} sub={`${stats?.uniqueIpsToday ?? 0} unique IPs`} />
              <StatCard icon="🎬" label="Total Exports" value={stats?.totalExports ?? '—'} />
              <StatCard
                icon="⚡"
                label="Avg Export Time"
                value={stats?.avgJobDurationMs ? `${(stats.avgJobDurationMs / 1000).toFixed(1)}s` : '—'}
                sub="backend FFmpeg"
              />
            </div>

            {/* Activity + Countries row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Sessions by Day */}
              <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-medium text-white">Sessions (last 30 days)</h3>
                  {stats && <Sparkline data={stats.sessionsByDay} />}
                </div>
                {stats ? (
                  <BarChart
                    data={stats.sessionsByDay.slice(-14).map(d => ({ label: d.date.slice(5), count: d.count }))}
                    max={maxSession}
                    color="#818cf8"
                  />
                ) : (
                  <div className="text-editor-text-muted text-xs">Loading…</div>
                )}
              </div>

              {/* Top Countries */}
              <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                <h3 className="text-sm font-medium text-white mb-4">Top Countries</h3>
                {stats?.topCountries.length ? (
                  <div className="flex flex-col gap-2">
                    {stats.topCountries.slice(0, 8).map((c, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs">
                        <CountryFlag code={c.countryCode} />
                        <span className="text-editor-text-secondary w-28 truncate">{c.country}</span>
                        <div className="flex-1 bg-white/5 rounded-full h-3 overflow-hidden">
                          <div
                            className="h-full rounded-full bg-indigo-500 transition-all duration-500"
                            style={{ width: `${(c.count / maxCountry) * 100}%` }}
                          />
                        </div>
                        <span className="text-editor-text-muted w-6 text-right">{c.count}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-editor-text-muted text-xs">No location data yet</div>
                )}
              </div>
            </div>

            {/* Device / OS / Browser / Resolution */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                <h3 className="text-xs font-medium text-editor-text-muted mb-3 uppercase tracking-wide">Device Type</h3>
                {stats && (
                  <DonutChart
                    data={stats.deviceBreakdown.map(d => ({ label: d.deviceType, count: d.count }))}
                    colors={{ Desktop: '#818cf8', Mobile: '#34d399', Tablet: '#f59e0b' }}
                  />
                )}
              </div>

              <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                <h3 className="text-xs font-medium text-editor-text-muted mb-3 uppercase tracking-wide">Operating System</h3>
                {stats && (
                  <DonutChart
                    data={stats.osBreakdown.map(d => ({ label: d.os, count: d.count }))}
                    colors={OS_COLORS}
                  />
                )}
              </div>

              <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                <h3 className="text-xs font-medium text-editor-text-muted mb-3 uppercase tracking-wide">Browser</h3>
                {stats && (
                  <DonutChart
                    data={stats.browserBreakdown.map(d => ({ label: d.browser, count: d.count }))}
                    colors={BROWSER_COLORS}
                  />
                )}
              </div>

              <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                <h3 className="text-xs font-medium text-editor-text-muted mb-3 uppercase tracking-wide">Export Resolution</h3>
                {stats && (
                  <DonutChart
                    data={stats.resolutionBreakdown.map(d => ({ label: d.resolution, count: d.count }))}
                    colors={{ '1080p': '#818cf8', '720p': '#34d399', '4k': '#f59e0b', 'original': '#f87171' }}
                  />
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── SESSIONS TAB ── */}
        {activeTab === 'sessions' && (
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-editor-text-muted">{sessionTotal} total sessions</p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="text-xs px-3 py-1.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg disabled:opacity-30 transition-colors"
                >
                  ← Prev
                </button>
                <span className="text-xs text-editor-text-muted px-2 py-1.5">Page {page}</span>
                <button
                  onClick={() => setPage(p => p + 1)}
                  disabled={sessions.length < 30}
                  className="text-xs px-3 py-1.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg disabled:opacity-30 transition-colors"
                >
                  Next →
                </button>
              </div>
            </div>

            <div className="overflow-x-auto rounded-xl border border-white/10">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-white/5 text-editor-text-muted uppercase tracking-wide text-left">
                    <th className="px-4 py-3 font-medium">Time</th>
                    <th className="px-4 py-3 font-medium">IP</th>
                    <th className="px-4 py-3 font-medium">Location</th>
                    <th className="px-4 py-3 font-medium">Device</th>
                    <th className="px-4 py-3 font-medium">OS</th>
                    <th className="px-4 py-3 font-medium">Browser</th>
                    <th className="px-4 py-3 font-medium">Endpoint</th>
                    <th className="px-4 py-3 font-medium">Export</th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.map((s, i) => (
                    <tr key={s.id} className={`border-t border-white/5 hover:bg-white/5 transition-colors ${i % 2 === 0 ? '' : 'bg-white/[0.02]'}`}>
                      <td className="px-4 py-2.5 text-editor-text-muted whitespace-nowrap">
                        {new Date(s.timestamp).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </td>
                      <td className="px-4 py-2.5 font-mono text-editor-text-secondary">{s.ip}</td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center">
                          {s.countryCode && <CountryFlag code={s.countryCode} />}
                          <span className="text-white">{s.city || s.country || 'Unknown'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-editor-text-secondary">
                        {DEVICE_ICONS[s.deviceType] ?? '💻'} {s.deviceType}
                      </td>
                      <td className="px-4 py-2.5 text-editor-text-secondary">{s.os} {s.osVersion}</td>
                      <td className="px-4 py-2.5 text-editor-text-secondary">{s.browser}</td>
                      <td className="px-4 py-2.5">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                          s.endpoint === '/api/render' ? 'bg-indigo-500/20 text-indigo-300'
                          : s.endpoint === '/api/upload' ? 'bg-green-500/20 text-green-300'
                          : 'bg-white/10 text-editor-text-muted'
                        }`}>
                          {s.endpoint}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-editor-text-muted">
                        {s.exportResolution && (
                          <span className="bg-white/10 px-1.5 py-0.5 rounded text-[10px]">
                            {s.exportResolution} · {s.exportQuality}
                            {s.jobDurationMs && ` · ${(s.jobDurationMs / 1000).toFixed(1)}s`}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {sessions.length === 0 && (
                    <tr>
                      <td colSpan={8} className="px-4 py-8 text-center text-editor-text-muted">
                        No sessions recorded yet. Use the app to start tracking.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── MAP TAB ── */}
        {activeTab === 'map' && (
          <div className="flex flex-col gap-4">
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <h3 className="text-sm font-medium text-white mb-4">User Locations</h3>
              {/* SVG World Map with user dots */}
              <WorldMap token={token} />
            </div>

            {/* Country table */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <h3 className="text-sm font-medium text-white mb-3">Sessions by Country</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                {(stats?.topCountries ?? []).map((c, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs bg-white/5 rounded-lg px-3 py-2">
                    <CountryFlag code={c.countryCode} />
                    <span className="text-white flex-1 truncate">{c.country}</span>
                    <span className="text-indigo-400 font-semibold">{c.count}</span>
                  </div>
                ))}
                {(stats?.topCountries ?? []).length === 0 && (
                  <div className="col-span-4 text-center text-editor-text-muted text-sm py-8">
                    No location data yet. Use the app to start tracking.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── World Map Component ─────────────────────────────────────────────────────

function WorldMap({ token }: { token: string }) {
  const [points, setPoints] = useState<{ lat: number; lon: number; count: number; city: string; country: string }[]>([]);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/admin/map`, { headers: { 'x-admin-token': token } })
      .then(r => r.json())
      .then(d => { if (d.success) setPoints(d.data as typeof points); })
      .catch(() => {/* ignore */});
  }, [token]);

  // Simple equirectangular projection
  const toSVG = (lat: number, lon: number, w: number, h: number) => ({
    x: ((lon + 180) / 360) * w,
    y: ((90 - lat) / 180) * h,
  });

  const W = 800;
  const H = 400;
  const maxCount = Math.max(...points.map(p => p.count), 1);

  return (
    <div className="relative w-full overflow-x-auto">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full rounded-lg"
        style={{ background: 'linear-gradient(135deg, #0f1520 0%, #1a2035 100%)' }}
      >
        {/* Simple world outline hint */}
        <rect x={0} y={0} width={W} height={H} fill="transparent" />
        <text x={W / 2} y={H / 2} textAnchor="middle" fill="#ffffff10" fontSize="11" fontFamily="monospace">
          {points.length === 0 ? 'No location data yet' : ''}
        </text>

        {/* Graticule lines */}
        {[-60, -30, 0, 30, 60].map(lat => {
          const y = ((90 - lat) / 180) * H;
          return <line key={lat} x1={0} y1={y} x2={W} y2={y} stroke="#ffffff08" strokeWidth="0.5" />;
        })}
        {[-120, -60, 0, 60, 120].map(lon => {
          const x = ((lon + 180) / 360) * W;
          return <line key={lon} x1={x} y1={0} x2={x} y2={H} stroke="#ffffff08" strokeWidth="0.5" />;
        })}

        {/* User dots */}
        {points.map((p, i) => {
          const pos = toSVG(p.lat, p.lon, W, H);
          const r = 3 + (p.count / maxCount) * 12;
          const opacity = 0.5 + (p.count / maxCount) * 0.5;
          return (
            <g key={i}>
              <circle
                cx={pos.x} cy={pos.y} r={r}
                fill="#818cf8"
                opacity={opacity}
                style={{ filter: 'blur(1px)' }}
              />
              <circle cx={pos.x} cy={pos.y} r={2.5} fill="white" opacity={0.9} />
              <title>{p.city}, {p.country} ({p.count} sessions)</title>
            </g>
          );
        })}
      </svg>
      {points.length > 0 && (
        <div className="mt-2 flex items-center gap-2 text-xs text-editor-text-muted">
          <span className="w-2.5 h-2.5 rounded-full bg-indigo-400 inline-block" />
          Dot size = session count. Hover for city details.
          <span className="ml-auto">{points.length} unique locations</span>
        </div>
      )}
    </div>
  );
}

export default AdminPage;
